import { Collapse, css, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useContext, useState } from 'react'
import { Button } from 'components/common/Button'
import { raceCategoriesLevel1 } from 'helpers/raceCategoryLevels'
import { unifiedColors } from 'styles/colors'
import { themeUtils } from 'styles/theme'
import { Checkbox, ExpandMore, FormControlLabel } from './FilterCategory.style'
import { Filter, FiltersContext } from './FiltersContext'

interface FilterCategoryProps {
  title: keyof typeof raceCategoriesLevel1
  filters: readonly Filter[]
  initiallyVisibleCount: number
}

interface FilterProps {
  filter: Filter & { count: number }
  handleCheckboxChange: (filter: Filter) => void
}

const FilterComponent = ({ filter, handleCheckboxChange }: FilterProps) => {
  const { t } = useTranslation('common')

  return (
    <Stack
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      key={filter.key}
      px={1}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={filter.checked}
            onChange={() => handleCheckboxChange(filter)}
          />
        }
        label={
          <Typography fontSize={14} fontWeight={600} noWrap>
            {t(filter.key)}
          </Typography>
        }
      />
      <Typography variant="body2" color={unifiedColors.label.subdued}>
        {filter.count}
      </Typography>
    </Stack>
  )
}

export const FilterCategory = ({
  title,
  filters,
  initiallyVisibleCount
}: FilterCategoryProps) => {
  const { t } = useTranslation(['common', 'home'])
  const [expanded, setExpanded] = useState(false)
  const isMobile = useMediaQuery(themeUtils.maxWidth.md)
  const { items, setFilters } = useContext(FiltersContext)

  const displayedFilters = filters.map((filter) => ({
    ...filter,
    count: items.find((item) => item.value === filter.key)?.count ?? 0
  }))
  const visibleFilters = displayedFilters.slice(0, initiallyVisibleCount)
  const hiddenFilters = displayedFilters.slice(initiallyVisibleCount)

  const toggleExpanded = () => setExpanded((prev) => !prev)
  const handleCheckboxChange = (filter: Filter) => {
    setFilters((prev) =>
      prev.map((f) =>
        f.key === filter.key ? { ...f, checked: !f.checked } : f
      )
    )
  }
  const handleAllCheckboxChange = () => {
    const allChecked = filters.every(({ checked }) => checked)
    setFilters((prev) =>
      prev.map((f) => ({
        ...f,
        checked: filters.map((t) => t.key).includes(f.key)
          ? !allChecked
          : f.checked
      }))
    )
  }

  return (
    <Stack>
      <FormControlLabel
        control={
          <Checkbox
            checked={filters.every(({ checked }) => checked)}
            onChange={handleAllCheckboxChange}
          />
        }
        label={
          <Typography noWrap fontWeight={700} lineHeight={1.3} fontSize={16}>
            {t(title)}
          </Typography>
        }
      />

      {visibleFilters.map((filter) => (
        <FilterComponent
          key={filter.key}
          filter={filter}
          handleCheckboxChange={handleCheckboxChange}
        />
      ))}

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack gap={0.25}>
          {hiddenFilters.map((filter) => (
            <FilterComponent
              key={filter.key}
              filter={filter}
              handleCheckboxChange={handleCheckboxChange}
            />
          ))}
        </Stack>
      </Collapse>

      {hiddenFilters.length > 0 &&
        (isMobile ? (
          <Button
            fullWidthOnMobile
            css={css`
              margin-top: 8px;
            `}
            label={t(`home:filters.${expanded ? 'seeLess' : 'seeMore'}`)}
            onClick={toggleExpanded}
            rightIcon={
              <ExpandMore
                $expand={expanded}
                aria-expanded={expanded}
                aria-label="show more"
              />
            }
          />
        ) : (
          <Stack
            px={1}
            pt={1}
            gap={0.25}
            flexDirection="row"
            alignItems="center"
            sx={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={toggleExpanded}
          >
            <Typography fontWeight={600} variant="body1">
              {t(`home:filters.${expanded ? 'seeLess' : 'seeMore'}`)} (
              {hiddenFilters.length})
            </Typography>
            <ExpandMore
              $expand={expanded}
              aria-expanded={expanded}
              aria-label="show more"
            />
          </Stack>
        ))}
    </Stack>
  )
}
